import { ROLE_LABELS, ROLES_DATA } from "@app/components/members/Roles";
import { formatDate } from "@app/lib/i18n/format";
import { useMemberDetails } from "@app/lib/swr/assistants";
import type { RoleType, WorkspaceType } from "@app/types/user";
import {
  Avatar,
  Chip,
  ContentMessage,
  Lock01,
  Separator,
  Sheet,
  SheetContent,
  SheetFooter,
  SheetTitle,
  Spinner,
} from "@dust-tt/sparkle";
import { Trans, useLingui } from "@lingui/react/macro";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

type MemberDetailsProps = {
  owner: WorkspaceType;
  onClose: () => void;
  userId: string | null;
};

const formatMembershipDate = (dateString: string | null) => {
  if (!dateString) {
    return null;
  }
  const date = new Date(dateString);
  return formatDate(date, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const getRoleBadgeColor = (
  role: RoleType
): "info" | "warning" | "success" | "primary" | "highlight" => {
  if (role === "none") {
    return "primary";
  }
  return ROLES_DATA[role].color;
};

export function MemberDetails({ userId, onClose, owner }: MemberDetailsProps) {
  const { t } = useLingui();
  const { userDetails, isMembersLoading, isMembersError } = useMemberDetails({
    workspaceId: owner.sId,
    userIds: userId ? [userId] : [],
  });
  const leftDate = formatMembershipDate(userDetails?.endAt ?? null);
  const joinedDate = formatMembershipDate(userDetails?.startAt ?? null);

  return (
    <Sheet open={!!userId} onOpenChange={onClose}>
      <SheetContent>
        <VisuallyHidden>
          <SheetTitle />
        </VisuallyHidden>
        {isMembersLoading ? (
          <div className="flex h-full w-full items-center justify-center">
            <Spinner size="lg" />
          </div>
        ) : isMembersError ? (
          <ContentMessage title={t`Not available`} icon={Lock01} size="md">
            <Trans>This user is not available.</Trans>
          </ContentMessage>
        ) : (
          userDetails && (
            <div className="flex h-full w-full flex-col items-center pt-8">
              <div className="flex w-full max-w-sm flex-col items-center gap-6">
                {/* Avatar with role badge */}

                <div className="relative flex flex-col items-center gap-3">
                  <Avatar
                    name={userDetails.fullName ?? t`User avatar`}
                    visual={userDetails.image ?? undefined}
                    size="xl"
                    isRounded
                    className={
                      userDetails.revoked ? "opacity-50 grayscale" : undefined
                    }
                  />
                  <Chip
                    size="xs"
                    color={
                      userDetails.revoked
                        ? "primary"
                        : getRoleBadgeColor(userDetails.role)
                    }
                    label={
                      userDetails.revoked
                        ? t`Former member`
                        : t(ROLE_LABELS[userDetails.role])
                    }
                    className="absolute -bottom-3 shadow-sm"
                  />
                </div>
                <div className="flex flex-col items-center gap-1">
                  <h2 className="text-xl font-semibold text-foreground">
                    {userDetails.fullName}
                  </h2>
                  {(userDetails.startAt ?? userDetails.endAt) && (
                    <p className="text-sm text-muted-foreground">
                      {userDetails.revoked && leftDate
                        ? t`Left the workspace: ${leftDate}`
                        : joinedDate
                          ? t`Joined the workspace: ${joinedDate}`
                          : null}
                    </p>
                  )}
                </div>

                <Separator />
                <div className="grid w-full grid-cols-2 gap-4">
                  <div className="col-span-1">
                    <div className="text-xs font-semibold tracking-wide text-muted-foreground">
                      <Trans>Username</Trans>
                    </div>
                    <div className="mt-1 text-sm text-foreground">
                      {userDetails.username}
                    </div>
                  </div>
                  <div className="col-span-1">
                    <div className="text-xs font-semibold tracking-wide text-muted-foreground">
                      <Trans>Full name</Trans>
                    </div>
                    <div className="mt-1 text-sm text-foreground">
                      {userDetails.fullName}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-xs font-semibold tracking-wide text-muted-foreground">
                      <Trans>Email</Trans>
                    </div>
                    <div className="mt-1 text-sm text-foreground">
                      {userDetails.email}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        )}
        <SheetFooter
          leftButtonProps={{
            label: t`Close`,
            variant: "outline",
          }}
        />
      </SheetContent>
    </Sheet>
  );
}
